import type { CostContract, CostExpense, CostExpenseFilter, CostInvoice, CostInvoiceFilter, CostInvoiceStatus } from '../types'
import { compareMoneyStrings, sumMoneyStrings, type DraftLine } from './contracts'
import { percentFieldProblem } from './applications'
import type { ModuleActions } from './permissions'

// ---- Rights (spec §1.4, §7.1) ----

export interface InvoiceAffordances {
  create: boolean
  editHeader: boolean
  editLines: boolean
  remove: boolean
  approve: boolean
  markPaid: boolean
  backToPending: boolean
  backToApproved: boolean
  addAttachment: boolean
  removeAttachment: boolean
}

/** Only PENDING invoices are edited or deleted; every status move needs APPROVE. `i` is null on the register. */
export function invoiceAffordances(a: ModuleActions, i: Pick<CostInvoice, 'status' | 'applicationId'> | null): InvoiceAffordances {
  const pending = i?.status === 'PENDING'
  return {
    create: a.canCreate,
    editHeader: a.canEdit && pending,
    editLines: a.canEdit && pending && !i?.applicationId,
    remove: a.canDelete && pending,
    approve: a.canApprove && pending,
    markPaid: a.canApprove && i?.status === 'APPROVED',
    backToPending: a.canApprove && i?.status === 'APPROVED',
    backToApproved: a.canApprove && i?.status === 'PAID',
    addAttachment: a.canCreate && !!i,
    removeAttachment: a.canDelete && !!i,
  }
}

export interface ExpenseAffordances { create: boolean; edit: boolean; remove: boolean; addAttachment: boolean; removeAttachment: boolean }
export const expenseAffordances = (a: ModuleActions): ExpenseAffordances =>
  ({ create: a.canCreate, edit: a.canEdit, remove: a.canDelete, addAttachment: a.canCreate, removeAttachment: a.canDelete })

// ---- Forms ----

const pad = (n: number) => String(n).padStart(2, '0')
/** Today as the person's local calendar date (YYYY-MM-DD). */
export const todayIso = (now: Date = new Date()): string => `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`

export interface InvoiceForm { contractId: string | null; number: string; issueDate: string | null; dueDate: string | null; amount: string | null; amountValid: boolean; note: string; retentionPercent: string | null; retentionValid: boolean }
export const emptyInvoiceForm = (contractId: string | null, today: string, retentionPercent: string | null = null): InvoiceForm =>
  ({ contractId, number: '', issueDate: today, dueDate: null, amount: null, amountValid: true, note: '', retentionPercent, retentionValid: true })
export const formFromInvoice = (i: CostInvoice): InvoiceForm =>
  ({ contractId: i.contractId, number: i.number, issueDate: i.issueDate, dueDate: i.dueDate, amount: i.amount, amountValid: true, note: i.note ?? '', retentionPercent: i.retentionPercent, retentionValid: true })

/** The first problem as an i18n key (the server checks the same), or null. The amount is asked for only on create. */
export function invoiceFormProblem(f: InvoiceForm, mode: 'create' | 'edit'): string | null {
  if (mode === 'create' && !f.contractId) return 'CostControl_invoice_contract_required'
  if (!f.number.trim()) return 'CostControl_error_invoice_number_required'
  if (!f.issueDate) return 'CostControl_error_issue_date_required'
  const retention = percentFieldProblem(f.retentionPercent, f.retentionValid)
  if (retention) return retention
  if (mode === 'create') {
    if (!f.amountValid) return 'CostControl_error_bad_amount'
    if (f.amount === null || compareMoneyStrings(f.amount, '0') <= 0) return 'CostControl_error_amount_positive'
  }
  if (f.dueDate && f.dueDate < f.issueDate) return 'CostControl_error_dates_order'
  return null
}

/** Form → API input: the contract and the amount (the server prefills the lines from it) only on create. */
export function invoiceInput(f: InvoiceForm, mode: 'create' | 'edit'): Record<string, unknown> {
  const header = { number: f.number.trim(), issueDate: f.issueDate, dueDate: f.dueDate, note: f.note.trim() || null, retentionPercent: f.retentionPercent }
  return mode === 'create' ? { contractId: f.contractId, ...header, amount: f.amount } : header
}

/** Contracts an invoice can bill (spec §1.4): subcontracts that are ACTIVE or CLOSED. */
export const invoiceContractOptions = <C extends Pick<CostContract, 'kind' | 'status'>>(contracts: C[]): C[] =>
  contracts.filter((c) => c.kind === 'SUB' && (c.status === 'ACTIVE' || c.status === 'CLOSED'))

export interface ExpenseForm { nodeId: string | null; description: string; supplierName: string; date: string | null; amount: string | null; amountValid: boolean }
export const emptyExpenseForm = (today: string): ExpenseForm => ({ nodeId: null, description: '', supplierName: '', date: today, amount: null, amountValid: true })
export const formFromExpense = (e: CostExpense): ExpenseForm =>
  ({ nodeId: e.nodeId, description: e.description, supplierName: e.supplierName ?? '', date: e.date, amount: e.amount, amountValid: true })

export function expenseFormProblem(f: ExpenseForm): string | null {
  if (!f.nodeId) return 'CostControl_expense_item_required'
  if (!f.description.trim()) return 'CostControl_error_description_required'
  if (!f.date) return 'CostControl_expense_date_required'
  if (!f.amountValid) return 'CostControl_error_bad_amount'
  if (f.amount === null || compareMoneyStrings(f.amount, '0') <= 0) return 'CostControl_error_amount_positive'
  return null
}
export const expenseInput = (f: ExpenseForm): Record<string, unknown> =>
  ({ nodeId: f.nodeId, description: f.description.trim(), supplierName: f.supplierName.trim() || null, date: f.date, amount: f.amount })

// ---- Invoice lines editor ----

/** Running total and the first problem (an i18n key), mirroring the server's checkInvoiceLines. */
export function checkInvoiceDraftLines(lines: DraftLine[], allowedIds: string[]): { total: string; problem: string | null } {
  const total = sumMoneyStrings(lines.filter((l) => l.valid && l.amount !== null).map((l) => l.amount as string))
  const allowed = new Set(allowedIds)
  const nodeIds = lines.map((l) => l.nodeId)
  let problem: string | null = null
  if (!lines.length) problem = 'CostControl_lines_problem_empty'
  else if (lines.some((l) => !l.valid)) problem = 'CostControl_lines_problem_invalid'
  else if (lines.some((l) => l.amount === null || compareMoneyStrings(l.amount, '0') <= 0)) problem = 'CostControl_lines_problem_positive'
  else if (new Set(nodeIds).size !== nodeIds.length) problem = 'CostControl_lines_problem_duplicate_item'
  else if (nodeIds.some((id) => !allowed.has(id))) problem = 'CostControl_lines_problem_not_allocated'
  return { total, problem }
}

// ---- Ledger: invoices and expenses in one list (spec §10) ----

export type LedgerKind = 'INVOICE' | 'EXPENSE'
/** One ledger row: an invoice (title = its number, detail = its contract) or an expense (title = its description, detail = its item). */
export interface LedgerRow {
  kind: LedgerKind
  id: string
  date: string
  title: string
  detail: string
  counterparty: string | null
  status: CostInvoiceStatus | null
  amount: string
  /** An invoice's amount less retention; null for an expense. */
  payable: string | null
}

/** Newest first; on the same day invoices before expenses, then by title. */
export function ledgerRows(invoices: CostInvoice[], expenses: CostExpense[]): LedgerRow[] {
  const rows: LedgerRow[] = [
    ...invoices.map((i): LedgerRow => ({ kind: 'INVOICE', id: i.id, date: i.issueDate, title: i.number, detail: i.contractName, counterparty: i.counterpartyName, status: i.status, amount: i.amount, payable: i.payable })),
    ...expenses.map((e): LedgerRow => ({
      kind: 'EXPENSE', id: e.id, date: e.date, title: e.description, detail: e.path ? `${e.path} / ${e.nodeName}` : e.nodeName,
      counterparty: e.supplierName, status: null, amount: e.amount, payable: null,
    })),
  ]
  const kindOrder: Record<LedgerKind, number> = { INVOICE: 0, EXPENSE: 1 }
  return rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)
    || kindOrder[a.kind] - kindOrder[b.kind]
    || a.title.localeCompare(b.title))
}

export interface LedgerFilterState { kind: LedgerKind | null; status: CostInvoiceStatus | null; contractId: string | null; search: string }
export const EMPTY_LEDGER_FILTER: LedgerFilterState = { kind: null, status: null, contractId: null, search: '' }
export const isLedgerFiltered = (s: LedgerFilterState): boolean => !!(s.kind || s.status || s.contractId || s.search.trim())

/** Which lists to ask for (null = skip): a status or a contract filter applies to invoices only, so expenses drop out. */
export function ledgerQueries(s: LedgerFilterState, search: string): { invoices: CostInvoiceFilter | null; expenses: CostExpenseFilter | null } {
  const text = search.trim()
  const invoices: CostInvoiceFilter = {}
  if (s.status) invoices.status = s.status
  if (s.contractId) invoices.contractId = s.contractId
  if (text) invoices.search = text
  const expenses: CostExpenseFilter = text ? { search: text } : {}
  return {
    invoices: s.kind === 'EXPENSE' ? null : invoices,
    expenses: s.kind === 'INVOICE' || s.status || s.contractId ? null : expenses,
  }
}
